import React, { useState, useEffect } from "react";
import axiosInstance from "../axiosConfig";
import Sidebar from "../components/Sidebar";
import Header from "../components/Header";
import { format } from "date-fns";
import Select from "react-select";
import Swal from "sweetalert2";

const WorkerRatingHistory = () => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Custom scrollbar style
  const scrollbarStyle = `
    .custom-scrollbar::-webkit-scrollbar {
      width: 6px;
      height: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f1f1f1;
      border-radius: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #c1c1c1;
      border-radius: 4px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #a8a8a8;
    }
  `;
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("");
  const [selectedWorker, setSelectedWorker] = useState("");
  const [isSidebarVisible, setIsSidebarVisible] = useState(true);
  const [selectedRows, setSelectedRows] = useState([]);

  const fetchHistory = async () => {
    try {
      const response = await axiosInstance.get("/api/worker-allocation-history");
      setHistory(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching worker history:", error);
      setLoading(false);
    }
  };

  const handleSelectRow = (id) => {
    setSelectedRows(prev => 
      prev.includes(id) ? prev.filter(rId => rId !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedRows(filteredHistory.map(r => r.id));
    } else {
      setSelectedRows([]);
    }
  };

  const handleDelete = async (id) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "You won't be able to revert this!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!"
    });

    if (result.isConfirmed) {
      try {
        await axiosInstance.delete(`/api/worker-allocation-history/${id}`);
        setHistory(prev => prev.filter(r => r.id !== id));
        setSelectedRows(prev => prev.filter(rId => rId !== id));
        Swal.fire("Deleted!", "The record has been deleted.", "success");
      } catch (error) {
        console.error("Error deleting record:", error);
        Swal.fire("Error!", "Failed to delete record.", "error");
      }
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) return;

    const result = await Swal.fire({
      title: "Delete multiple records?",
      text: `You are about to delete ${selectedRows.length} records. This cannot be undone!`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete them!"
    });

    if (result.isConfirmed) {
      try {
        await Promise.all(selectedRows.map(id => axiosInstance.delete(`/api/worker-allocation-history/${id}`)));
        setHistory(prev => prev.filter(r => !selectedRows.includes(r.id)));
        setSelectedRows([]);
        Swal.fire("Deleted!", "The selected records have been deleted.", "success");
      } catch (error) {
        console.error("Error deleting records:", error);
        Swal.fire("Error!", "Failed to delete some records.", "error");
        fetchHistory();
      }
    }
  };

  useEffect(() => {
    fetchHistory();
  }, []);

  const toggleSidebar = () => setIsSidebarVisible(!isSidebarVisible);

  const uniqueDepartments = [...new Set(history.map(r => r.to_department_id).filter(Boolean))].sort();
  const departmentOptions = [
    { value: "", label: "All Departments" },
    ...uniqueDepartments.map(dept => ({ value: dept, label: dept }))
  ];

  const uniqueWorkers = Array.from(new Map(history.filter(r => r.worker_id).map(r => [r.worker_id, r.worker_name])).entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
  const workerOptions = [
    { value: "", label: "All Workers" },
    ...uniqueWorkers.map(w => ({ value: w.id, label: `${w.name} (#${w.id})` }))
  ];

  const filteredHistory = history.filter((record) => {
    const term = searchTerm.toLowerCase();
    
    // 1. Search Term
    const matchesSearch = !term || (
      (record.worker_name && record.worker_name.toLowerCase().includes(term)) ||
      (record.worker_id && String(record.worker_id).toLowerCase().includes(term)) ||
      (record.from_department_id && record.from_department_id.toLowerCase().includes(term)) ||
      (record.to_department_id && record.to_department_id.toLowerCase().includes(term)) ||
      (record.satisfaction_status && record.satisfaction_status.toLowerCase().includes(term))
    );

    // 2. Date Filter
    let matchesDate = true;
    if (selectedDate) {
      // get local date string YYYY-MM-DD from record.assigned_at
      const recordDateObj = new Date(record.assigned_at);
      // Adjust for local timezone offset to get local YYYY-MM-DD string
      const localDateStr = new Date(recordDateObj.getTime() - (recordDateObj.getTimezoneOffset() * 60000)).toISOString().split('T')[0];
      matchesDate = localDateStr === selectedDate;
    }

    // 3. Department / Supervisor
    const matchesDept = !selectedDepartment || record.to_department_id === selectedDepartment;

    // 4. Worker
    const matchesWorker = !selectedWorker || String(record.worker_id) === String(selectedWorker);

    return matchesSearch && matchesDate && matchesDept && matchesWorker;
  });

  const handleExportCSV = () => {
    if (filteredHistory.length === 0) return;
    
    const headers = "Date,Time,Assigned By,Worker ID,Worker Name,From Department,To Department,Notified Supervisor,Status,Confirmed By,WhatsApp Log,Rating,Rated By,Remarks";
    
    const rows = filteredHistory.map(record => {
      const dateStr = record.assigned_at ? format(new Date(record.assigned_at), "dd-MMM-yyyy") : "-";
      const timeStr = record.assigned_at ? format(new Date(record.assigned_at), "hh:mm a") : "-";
      
      return [
        `"${dateStr}"`,
        `"${timeStr}"`,
        `"${record.assigned_by || "-"}"`,
        `"${record.worker_id || ""}"`,
        `"${record.worker_name || ""}"`,
        `"${record.from_department_id || ""}"`,
        `"${record.to_department_id || ""}"`,
        `"${record.notified_supervisor || "-"}"`,
        `"${record.work_status || ""}"`,
        `"${record.confirmed_by || "-"}"`,
        `"${record.whatsapp_message_log ? record.whatsapp_message_log.replace(/"/g, '""').replace(/\n/g, ' ') : "No message"}"`,
        `"${record.satisfaction_status || "-"}"`,
        `"${record.rated_by || "-"}"`,
        `"${record.remarks ? record.remarks.replace(/"/g, '""').replace(/\n/g, ' ') : "-"}"`
      ].join(",");
    });
    
    const csvContent = [headers, ...rows].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", "Worker_Rating_History.csv");
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "Pending":
        return <span className="badge bg-warning text-dark px-3 py-2 rounded-pill"><i className="bi bi-hourglass-split me-1"></i>Pending</span>;
      case "Occupied":
        return <span className="badge bg-primary px-3 py-2 rounded-pill"><i className="bi bi-person-workspace me-1"></i>Occupied</span>;
      case "Completed":
        return <span className="badge bg-success px-3 py-2 rounded-pill"><i className="bi bi-check2-circle me-1"></i>Completed</span>;
      default:
        return <span className="badge bg-secondary px-3 py-2 rounded-pill">{status}</span>;
    }
  };

  const getSatisfactionBadge = (rating) => {
    if (!rating) return <span className="text-muted"><i className="bi bi-dash"></i></span>;
    
    const lowerRating = rating.toLowerCase();
    if (lowerRating.includes("good") || lowerRating.includes("excellent")) {
      return <span className="badge bg-success px-2 py-1"><i className="bi bi-emoji-smile me-1"></i>{rating}</span>;
    } else if (lowerRating.includes("bad") || lowerRating.includes("poor")) {
      return <span className="badge bg-danger px-2 py-1"><i className="bi bi-emoji-frown me-1"></i>{rating}</span>;
    } else {
      return <span className="badge bg-info text-dark px-2 py-1"><i className="bi bi-emoji-neutral me-1"></i>{rating}</span>;
    }
  };

  return (
    <div className="d-flex dashboard">
      <style>{scrollbarStyle}</style>
      <div className={isSidebarVisible ? "sidebar-container" : "sidebar-hidden"}>
        <Sidebar />
      </div>

      <div className="flex-grow-1 d-flex flex-column h-100" style={{ minWidth: 0, overflowX: "hidden" }}>
        <Header toggleSidebar={toggleSidebar} isSidebarVisible={isSidebarVisible} />

        <div className="p-4 flex-grow-1 overflow-auto bg-light">
          {/* Header Section */}
          <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-3">
            <div>
              <h2 style={{ fontSize: '24px', fontWeight: 800, color: '#1e293b', letterSpacing: '-0.5px', margin: '0 0 4px 0' }}>
                <i className="bi bi-clipboard-data-fill text-primary me-2"></i>
                Worker Ratings & History
              </h2>
              <p style={{ fontSize: '14px', color: '#64748b', margin: 0 }}>
                View complete assignment logs, WhatsApp messages, and supervisor ratings.
              </p>
            </div>
            <div className="d-flex gap-2">
              {selectedRows.length > 0 && (
                <button 
                  className="btn btn-danger rounded-pill px-4 shadow-sm fw-bold d-flex align-items-center"
                  onClick={handleBulkDelete}
                >
                  <i className="bi bi-trash-fill me-2 fs-5"></i>
                  Delete ({selectedRows.length})
                </button>
              )}
              <button 
                className="btn btn-success rounded-pill px-4 shadow-sm fw-bold d-flex align-items-center"
                onClick={handleExportCSV}
                disabled={filteredHistory.length === 0}
              >
                <i className="bi bi-file-earmark-excel-fill me-2 fs-5"></i>
                Export to Excel
              </button>
            </div>
          </div>

          {/* Filters Section */}
          <div className="card shadow-sm border-0 rounded-4 mb-4" style={{ background: 'linear-gradient(145deg, #ffffff, #f8fafc)' }}>
            <div className="card-body p-3">
              <div className="row g-3">
                {/* Search */}
                <div className="col-lg-3 col-md-6">
                  <div className="input-group h-100">
                    <span className="input-group-text bg-white border-end-0 border-light-subtle rounded-start-pill ps-3">
                      <i className="bi bi-search text-muted"></i>
                    </span>
                    <input
                      type="text"
                      className="form-control border-start-0 border-light-subtle rounded-end-pill py-2"
                      placeholder="Search anything..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      style={{ boxShadow: 'none' }}
                    />
                  </div>
                </div>

                {/* Date Filter */}
                <div className="col-lg-3 col-md-6">
                  <div className="input-group h-100">
                    <span className="input-group-text bg-white border-end-0 border-light-subtle rounded-start-pill ps-3">
                      <i className="bi bi-calendar3 text-muted"></i>
                    </span>
                    <input
                      type="date"
                      className="form-control border-start-0 border-light-subtle rounded-end-pill py-2 text-muted"
                      value={selectedDate}
                      onChange={(e) => setSelectedDate(e.target.value)}
                      style={{ boxShadow: 'none' }}
                    />
                  </div>
                </div>

                {/* Supervisor/Department Filter */}
                <div className="col-lg-3 col-md-6">
                  <div className="input-group h-100">
                    <span className="input-group-text bg-white border-end-0 border-light-subtle rounded-start-pill ps-3">
                      <i className="bi bi-building text-muted"></i>
                    </span>
                    <div className="flex-grow-1 bg-white border border-start-0 border-light-subtle rounded-end-pill d-flex align-items-center pe-2">
                      <Select 
                        options={departmentOptions}
                        value={departmentOptions.find(opt => opt.value === selectedDepartment) || departmentOptions[0]}
                        onChange={(selectedOption) => setSelectedDepartment(selectedOption ? selectedOption.value : "")}
                        className="w-100"
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            minHeight: '38px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>

                {/* Worker Filter */}
                <div className="col-lg-3 col-md-6">
                  <div className="input-group h-100">
                    <span className="input-group-text bg-white border-end-0 border-light-subtle rounded-start-pill ps-3">
                      <i className="bi bi-person-badge text-muted"></i>
                    </span>
                    <div className="flex-grow-1 bg-white border border-start-0 border-light-subtle rounded-end-pill d-flex align-items-center pe-2">
                      <Select 
                        options={workerOptions}
                        value={workerOptions.find(opt => opt.value === String(selectedWorker)) || workerOptions[0]}
                        onChange={(selectedOption) => setSelectedWorker(selectedOption ? selectedOption.value : "")}
                        className="w-100"
                        styles={{
                          control: (base) => ({
                            ...base,
                            border: 'none',
                            boxShadow: 'none',
                            backgroundColor: 'transparent',
                            minHeight: '38px'
                          }),
                          menu: (base) => ({
                            ...base,
                            zIndex: 9999
                          })
                        }}
                      />
                    </div>
                  </div>
                </div>

              </div>
            </div>
          </div>

          {/* Data Table */}
          <div className="card shadow-sm border-0 rounded-4 overflow-hidden">
            <div className="table-responsive custom-scrollbar" style={{ maxHeight: 'calc(100vh - 280px)' }}>
              <table className="table table-hover align-middle mb-0" style={{ fontSize: '0.9rem' }}>
                <thead className="table-dark sticky-top" style={{ zIndex: 0 }}>
                  <tr>
                    <th className="py-3 ps-4 border-0">
                      <input 
                        type="checkbox" 
                        className="form-check-input"
                        checked={filteredHistory.length > 0 && selectedRows.length === filteredHistory.length}
                        onChange={handleSelectAll}
                      />
                    </th>
                    <th className="py-3 border-0 text-nowrap">Date & Time</th>
                    <th className="py-3 border-0 text-nowrap">Assigned By</th>
                    <th className="py-3 border-0 text-nowrap">Worker Info</th>
                    <th className="py-3 border-0 text-nowrap">Movement</th>
                    <th className="py-3 border-0 text-nowrap">Notified Supervisor</th>
                    <th className="py-3 border-0 text-nowrap">Status</th>
                    <th className="py-3 border-0 text-nowrap">Confirmed By</th>
                    <th className="py-3 border-0" style={{ minWidth: '300px' }}>WhatsApp Log</th>
                    <th className="py-3 border-0 text-center text-nowrap">Rating & Remarks</th>
                    <th className="py-3 pe-4 border-0 text-center text-nowrap">Action</th>
                  </tr>
                </thead>
                <tbody className="bg-white">
                  {loading ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5">
                        <div className="spinner-border text-primary" role="status">
                          <span className="visually-hidden">Loading...</span>
                        </div>
                      </td>
                    </tr>
                  ) : filteredHistory.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="text-center py-5 text-muted">
                        <i className="bi bi-inbox fs-2 d-block mb-2"></i>
                        No history records found.
                      </td>
                    </tr>
                  ) : (
                    filteredHistory.map((record, index) => (
                      <tr key={record.id || index} style={{ transition: 'all 0.2s ease', backgroundColor: selectedRows.includes(record.id) ? '#fef2f2' : 'transparent' }}>
                        
                        {/* Checkbox */}
                        <td className="ps-4">
                          <input 
                            type="checkbox" 
                            className="form-check-input"
                            checked={selectedRows.includes(record.id)}
                            onChange={() => handleSelectRow(record.id)}
                          />
                        </td>

                        {/* Date */}
                        <td className="text-nowrap">
                          <div className="fw-bold text-dark">
                            {record.assigned_at ? format(new Date(record.assigned_at), "dd MMM, yyyy") : "-"}
                          </div>
                          <div className="text-muted small">
                            {record.assigned_at ? format(new Date(record.assigned_at), "hh:mm a") : ""}
                          </div>
                        </td>

                        {/* Assigned By */}
                        <td>
                          {record.assigned_by ? (
                            <div className="d-flex align-items-center">
                              <i className="bi bi-person-fill text-muted me-2"></i>
                              <span className="fw-medium text-dark">{record.assigned_by}</span>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* Worker */}
                        <td>
                          <div className="d-flex align-items-center">
                            <div className="avatar-circle bg-primary-subtle text-primary fw-bold me-3 rounded-circle d-flex align-items-center justify-content-center" style={{ width: '40px', height: '40px' }}>
                              {record.worker_name ? record.worker_name.charAt(0).toUpperCase() : "?"}
                            </div>
                            <div>
                              <div className="fw-bold text-dark">{record.worker_name || "Unknown"}</div>
                              <div className="text-muted small">ID: #{record.worker_id}</div>
                            </div>
                          </div>
                        </td>

                        {/* Movement */}
                        <td>
                          <div className="d-flex flex-column gap-1">
                            <span className="badge bg-light text-dark border"><i className="bi bi-box-arrow-right text-danger me-1"></i>{record.from_department_id || "N/A"}</span>
                            <i className="bi bi-arrow-down text-muted text-center" style={{ fontSize: '10px' }}></i>
                            <span className="badge bg-light text-dark border"><i className="bi bi-box-arrow-in-right text-success me-1"></i>{record.to_department_id || "N/A"}</span>
                          </div>
                        </td>

                        {/* Notified Supervisor */}
                        <td>
                          {record.notified_supervisor ? (
                            <div className="d-flex align-items-center">
                              <i className="bi bi-bell-fill text-warning me-2"></i>
                              <span className="fw-medium text-dark">{record.notified_supervisor}</span>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* Status */}
                        <td>
                          {getStatusBadge(record.work_status)}
                        </td>

                        {/* Confirmed By */}
                        <td>
                          {record.confirmed_by ? (
                            <div className="d-flex align-items-center">
                              <i className="bi bi-check-circle-fill text-success me-2"></i>
                              <span className="fw-medium text-dark">{record.confirmed_by}</span>
                            </div>
                          ) : (
                            <span className="text-muted">-</span>
                          )}
                        </td>

                        {/* WhatsApp Log */}
                        <td style={{ minWidth: '300px' }}>
                          {record.whatsapp_message_log ? (
                            <div 
                              className="p-2 bg-light rounded text-wrap text-break" 
                              style={{ 
                                fontSize: '0.8rem', 
                                maxHeight: '80px', 
                                overflowY: 'auto',
                                borderLeft: '3px solid #25D366'
                              }}
                            >
                              <i className="bi bi-whatsapp text-success me-1"></i>
                              {record.whatsapp_message_log}
                            </div>
                          ) : (
                            <span className="text-muted small fst-italic">No message sent</span>
                          )}
                        </td>

                        {/* Rating */}
                        <td>
                          <div className="d-flex flex-column align-items-center gap-2">
                            {getSatisfactionBadge(record.satisfaction_status)}
                            {record.remarks && (
                              <div className="small text-muted text-center w-100" style={{ maxWidth: '150px' }}>
                                "{record.remarks}"
                              </div>
                            )}
                          </div>
                        </td>

                        {/* Action */}
                        <td className="pe-4 text-center">
                          <button 
                            className="btn btn-sm btn-outline-danger border-0 rounded-circle"
                            onClick={() => handleDelete(record.id)}
                            title="Delete Record"
                          >
                            <i className="bi bi-trash"></i>
                          </button>
                        </td>

                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
          
        </div>
      </div>
    </div>
  );
};

export default WorkerRatingHistory;
